import React, { useState, useEffect, useCallback } from "react";
import { CheckCircle2, X, Clock, Wrench } from "lucide-react";

const CASES = [
  {
    code: "N47",
    label: "BMW N47",
    available: true,
    title: "BMW N47 motor nagyszervíz",
    sub: "1.6d / 2.0d dízel — vezérműlánc + alsó-felső csapágyazás",
    intro:
      "A jól ismert N47 dízel motorok klasszikus problémája: a hátul futó vezérműlánc nyúlása, sínek kopása és a csapágyak előrehaladott elhasználódása. Itt egy teljes körű, blokkbontásos felújítást dokumentáltunk.",
    bullets: [
      "Motor kiemelés, teljes szétszerelés a műhelyben",
      "Vezérműlánc, vezetősín, feszítő, vízpumpa csere — eredeti BMW alkatrészekkel",
      "Forgattyús-, hajtórúd- és vezetőcsapágyak ellenőrzése, mérése és cseréje",
      "Olajteknő, szelepfedél, tömítőkészlet csere — gyári előírás szerint",
      "ISTA / Rheingold adaptáció reset és próbaút utáni dokumentált átadás",
    ],
    photos: [
      {
        src: "/case-studies/n47/n47-blokk-csapagyak.jpg",
        caption: "Blokk alsó nézet — friss főtengely-csapágyak, hajtórudak",
      },
      {
        src: "/case-studies/n47/n47-vezermulanc.jpg",
        caption: "Új vezérműlánc-készlet beszerelés alatt — sín, feszítő, fogaskerék",
      },
    ],
  },
  {
    code: "N55",
    label: "BMW N55",
    available: true,
    title: "BMW N55 motor csapágyazás",
    sub: "3.0 turbó benzin — alsó csapágyhéjak felújítása",
    intro:
      "Az N55-ös motor klasszikus, magas futásteljesítménynél jelentkező problémája: a hajtórúd-csapágyak elhasználódása. Megfelelő olajnyomással és időben elvégzett csapágycserével a motor élettartama jelentősen meghosszabbítható — anélkül, hogy a motor blokkot ki kellene emelni az autóból.",
    bullets: [
      "Olajteknő leszerelése autóban — minimális szétszerelés",
      "Hajtórúd-csapágyhéjak (felső + alsó) ellenőrzése, kopásmérés",
      "Komplett csapágyhéj-készlet csere — eredeti BMW alkatrészekkel",
      "Hajtórúd-csavarok kötelező cseréje, gyári nyomatékkulcsra meghúzva",
      "Új olajteknő-tömítés, friss BMW Longlife-01 motorolaj és olajszűrő",
      "Próbaút utáni átvizsgálás — olajnyomás, motorhang, hibakód-ellenőrzés ISTA-val",
    ],
    photos: [
      {
        src: "/case-studies/n55/n55-blokk-bontva.jpg",
        caption: "N55 alulnézet — olajteknő leszerelve, hajtórúd-csapágy­fedelek hozzáférhetők",
      },
      {
        src: "/case-studies/n55/n55-csapagyhejak.jpg",
        caption: "Eredeti csapágyhéjak — felső és alsó halmazok, kopási nyomokkal",
      },
    ],
  },
];

export default function CaseStudies() {
  const [active, setActive] = useState(CASES[0].code);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const close = useCallback(() => setLightboxSrc(null), []);

  useEffect(() => {
    if (!lightboxSrc) return;
    const handler = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [lightboxSrc, close]);

  const current = CASES.find((c) => c.code === active);

  return (
    <section
      id="munkaink"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="bmw-case-studies"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span
              className="text-[0.7rem] font-bold uppercase tracking-[0.32em] text-[#0082D2]"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Munkáink — Case studies
            </span>
            <h2
              className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2.25rem, 5vw, 4rem)",
              }}
            >
              Nagyszervízek
              <br />
              <span className="text-[#0082D2]">motor­típus szerint.</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 leading-relaxed">
            Saját műhelyünkben dokumentált munkák — pontosan ezt fogja kapni
            Ön is, ha BMW-jét nálunk újítja fel.
          </p>
        </div>

        {/* Tabs */}
        <div
          className="flex flex-wrap gap-2 border-b border-white/10 mb-10"
          role="tablist"
          data-testid="case-studies-tabs"
        >
          {CASES.map((c) => {
            const isActive = c.code === active;
            return (
              <button
                key={c.code}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(c.code)}
                className={`relative px-5 sm:px-7 py-3 text-sm font-bold uppercase tracking-[0.18em] transition-colors ${
                  isActive
                    ? "text-white"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
                style={{ fontFamily: "Barlow Condensed" }}
                data-testid={`case-tab-${c.code.toLowerCase()}`}
              >
                <span className="flex items-center gap-2">
                  <Wrench className="w-3.5 h-3.5" strokeWidth={1.8} />
                  {c.label}
                  {!c.available && (
                    <span className="text-[0.55rem] font-bold uppercase tracking-[0.2em] text-zinc-600 border border-zinc-700 px-1.5 py-0.5 rounded-sm">
                      Hamarosan
                    </span>
                  )}
                </span>
                {isActive && (
                  <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-[#0082D2]" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div
          className="grid lg:grid-cols-12 gap-10"
          role="tabpanel"
          data-testid={`case-panel-${current.code.toLowerCase()}`}
        >
          {/* Left: text */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <div className="flex items-center gap-3 mb-4">
              <span className="m-stripe h-1 w-12" />
              <span
                className="text-[0.65rem] font-bold uppercase tracking-[0.28em] text-zinc-400"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Motorkód · {current.code}
              </span>
            </div>

            <h3
              className="font-black uppercase text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)",
              }}
            >
              {current.title}
            </h3>
            <p className="mt-2 text-sm text-[#0082D2] font-semibold uppercase tracking-[0.12em]">
              {current.sub}
            </p>
            <p className="mt-5 text-zinc-300 leading-relaxed">
              {current.intro}
            </p>

            {current.bullets.length > 0 && (
              <ul className="mt-6 space-y-3">
                {current.bullets.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-zinc-300 text-sm leading-relaxed"
                  >
                    <CheckCircle2
                      className="w-4 h-4 text-[#0082D2] flex-shrink-0 mt-0.5"
                      strokeWidth={2}
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Right: photos */}
          <div className="lg:col-span-7">
            {current.photos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {current.photos.map((p, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLightboxSrc(p.src)}
                    className="relative aspect-[3/4] bg-[#101010] border border-white/5 overflow-hidden group transition-all hover:border-[#0082D2]/60 cursor-zoom-in text-left"
                    data-testid={`case-photo-${current.code.toLowerCase()}-${idx}`}
                    aria-label={p.caption}
                  >
                    <img
                      src={p.src}
                      alt={`${current.title} — ${p.caption}`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <span className="block text-[0.6rem] font-bold uppercase tracking-[0.24em] text-[#0082D2] mb-1"
                        style={{ fontFamily: "Barlow Condensed" }}
                      >
                        {current.code} · {idx + 1}/{current.photos.length}
                      </span>
                      <span className="block text-sm text-white font-semibold leading-snug">
                        {p.caption}
                      </span>
                    </div>
                    <div className="absolute top-0 left-0 right-0 m-stripe h-[2px] opacity-40 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            ) : (
              <div
                className="aspect-[16/10] bg-[#0e0e0e] border border-dashed border-white/10 flex flex-col items-center justify-center text-center p-10"
                data-testid="case-empty-state"
              >
                <Clock className="w-8 h-8 text-zinc-600 mb-4" strokeWidth={1.4} />
                <p className="text-zinc-400 text-sm max-w-sm">
                  Hamarosan érkeznek a fotók erről a munkáról. Addig is hívjon
                  minket, ha hasonló munkát szeretne elvégeztetni!
                </p>
                <a
                  href="tel:+36308637476"
                  className="mt-5 text-[#0082D2] hover:text-white transition-colors text-sm font-bold uppercase tracking-[0.2em]"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  +36 30 863 7476
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={close}
          role="dialog"
          aria-modal="true"
          data-testid="case-lightbox"
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Bezárás"
            data-testid="case-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxSrc}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
